import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, MenuItem, TextField } from '@mui/material'

import { AppStateContext, UserContext } from '../../contexts'
import { Pocket } from '../../utils/dexie/db'
import { Customer, FormError } from '../../types'
import AccountIcon from '../../assets/icons/account-icon.svg'
import ContactsIcon from '../../assets/icons/contacts-icon.svg'
import { handleGetParsedValue, numberWithCommas } from '../../utils'
import { baseButtonTextWhiteBg, textFieldWhiteBg } from '../../theme/custom-styles'
import { countriesCurrencies } from '../../lib'

export const MakeIndividualPaymentForm = ({
  errors,
  customerData,
  amountToSend,
  selectedPocketId,
  handleSetPocketId,
  bankAccountsToSelect,
  selectedBankAccountData,
  handleSetBankAccountToSend,
  handleSetPaymentAmountToSend,
  handleOpenNewBankAccountDialog
}: {
  errors?: FormError
  amountToSend: string
  selectedPocketId: string
  customerData: Customer | null
  selectedBankAccountData: string
  bankAccountsToSelect: Array<string>
  handleOpenNewBankAccountDialog: () => void
  handleSetPocketId: (pocketIdToSet: string) => void
  handleSetBankAccountToSend: (bankAccountToSend: string) => void
  handleSetPaymentAmountToSend: (paymentAmountToSend: string) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry]

  const { userSession } = useContext(UserContext)
  const { currentPocketsData } = useContext(AppStateContext)
  const { t } = useTranslation(['individual-money-transfers-management'])

  const handleGetBankAccountDataToDisplay = (bankAccountDataToSelect: string) => {
    const bankAccountDataToSelectSplitted = bankAccountDataToSelect.split('-')

    if (bankAccountDataToSelectSplitted.splice(-2)[0] === 'breb') {
      const brebNumberAccount = bankAccountDataToSelect.split('-').splice(-4)[0]
      return `Bre-B - ${brebNumberAccount}`
    }

    if (bankAccountDataToSelect.split('-')[0]) {
      return `${bankAccountDataToSelect.split('-')[0]} - No. ${bankAccountDataToSelect.split('-')[1]}`
    }

    return `No. ${bankAccountDataToSelect.split('-')[1]}`
  }

  return (
    <div className="make-individual-payment-form-container">
      <div className="make-individual-payment-form-personal-data-container">
        <div className="make-individual-payment-form-personal-data-title">{t('newIndividualMoneyTransferModal.makeIndividualPaymentForm.mainInstruction')}</div>
        <div className="make-individual-payment-form-personal-data-square-container">
          <div className="make-individual-payment-form-personal-data-info">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
            <div>{customerData?.registeredFullName}</div>
          </div>
          <div className="make-individual-payment-form-personal-data-info">
            <img
              src={ContactsIcon}
              alt="contacts-icon"
            />
            <div>{customerData?.documentNumber}</div>
          </div>
        </div>
      </div>
      <div className="make-individual-payment-form-payment-data-container">
        <div className="bank-account-selection">
          {Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? (
            <>
              <div className="bank-account-select-field">
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  value={selectedBankAccountData || ''}
                  error={!!errors?.selectedBankAccount}
                  label={t('paymentForm.accountToSend')}
                  helperText={errors?.selectedBankAccount}
                  onChange={(event) => handleSetBankAccountToSend(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                >
                  {bankAccountsToSelect.map((bankAccountData: string) => {
                    return (
                      <MenuItem
                        key={bankAccountData}
                        value={bankAccountData}
                      >
                        {handleGetBankAccountDataToDisplay(bankAccountData)}
                      </MenuItem>
                    )
                  })}
                </TextField>
              </div>
              <div className="bank-account-button">
                <Button
                  fullWidth
                  variant="text"
                  sx={baseButtonTextWhiteBg}
                  className="actions-bar-button-action"
                  onClick={handleOpenNewBankAccountDialog}
                >
                  <div className="actions-bar-button-action-text">{t('paymentForm.addBankAccountShort')}</div>
                </Button>
              </div>
            </>
          ) : (
            <div className="no-bank-account-button">
              <Button
                fullWidth
                variant="text"
                sx={baseButtonTextWhiteBg}
                className="actions-bar-button-action"
                onClick={handleOpenNewBankAccountDialog}
              >
                <div className="actions-bar-button-action-text">{t('paymentForm.addBankAccount')}</div>
              </Button>
              {errors?.selectedBankAccount && (
                <div
                  style={{ marginTop: '0px' }}
                  className="error-message"
                >
                  {errors?.selectedBankAccount}
                </div>
              )}
            </div>
          )}
        </div>
        <TextField
          fullWidth
          variant="standard"
          sx={textFieldWhiteBg}
          value={amountToSend}
          error={!!errors?.amountToSend}
          helperText={errors?.amountToSend}
          label={`${t('paymentForm.amountToSend')} (${currency})`}
          onBlur={(event) => {
            if (event.target.value === '') {
              handleSetPaymentAmountToSend(`$ ${numberWithCommas(amountToSend)}`)
            }
          }}
          onChange={(event) => {
            const extractedNumber = event.target.value.startsWith('$') ? Number(event.target.value.slice(1).trim().replaceAll(',', '')) : Number(event.target.value.trim().replaceAll(',', ''))

            if (Number.isNaN(extractedNumber)) {
              return
            }

            if (event.target.value.trim() === '$' || event.target.value.trim() === '0') {
              return handleSetPaymentAmountToSend('')
            }

            const valueToSet = handleGetParsedValue(event)
            handleSetPaymentAmountToSend(valueToSet)
          }}
        />
        {userSession?.role === 'super-admin-client' && (
          <div
            style={{ padding: '0px' }}
            className="customers-add-new-form-personal-data-form-row"
          >
            <TextField
              select
              fullWidth
              variant="standard"
              value={selectedPocketId || ''}
              label={t('paymentForm.fundsFrom')}
              error={!!errors?.selectedPocketId}
              helperText={errors?.selectedPocketId}
              onChange={(event) => handleSetPocketId?.(event.target.value)}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select')}
              </MenuItem>
              {currentPocketsData?.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={String(pocketData.id)}
                    value={String(pocketData.id)}
                  >
                    {`${pocketData.name?.toUpperCase()} - $ ${numberWithCommas(Number(pocketData.balance))}`}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
        <div
          className="make-individual-payment-form-personal-data-title"
          style={{ padding: '0px 20px', boxSizing: 'border-box', fontSize: '12px', lineHeight: '150%', color: '#EF4444', fontWeight: '600' }}
        >
          {t('paymentForm.warning')}
        </div>
      </div>
    </div>
  )
}
